// PPAField – single form-row molecule used inside PriceSimulatorCreateEditModal.
// Renders a label + the right control for each input_type: string, date_range,
// singleselect, multiselect, boolean. project_filters options are fetched from
// `ppa/simulation/get_attribute_names`; all other fields use static options from inputConfig.

import React, { FC, useContext, useEffect, useLayoutEffect, useMemo } from 'react';

import { InfoCircleOutlined } from '@ant-design/icons';
import { Input, Switch, Tooltip } from 'antd';
import dayjs from 'dayjs';

import { CustomSelect } from 'components/molecules/PricingSimulator/DimSection';
import { MaxTagComponent } from 'components/molecules/PricingSimulator/Modals/CreateEditProjectContainer';
import { DateRangePicker } from 'components/molecules/PricingSimulator/DateRangePicker';
import { PPA_MAX_SIMULATION_WEEKS } from 'utils/ppaHelpers';

import { UserContext } from 'contexts/ConfigContext';
import { usePriceSimulatorV3Context } from 'contexts/PriceSimulatorV3Context';

import { getAccessIcon, getConfig } from 'utils/helperMethods';

import type { PPASelectOption } from 'types/ppa';

import {
    CONTROL_WIDTH,
    MAX_TAG_CHARACTER_LIMIT,
    PRESET_PERIOD_IDS,
    dedupeSelectValuesByKey,
    getPresetDisplayRange,
    getPresetSimulationSelectionJson,
} from './PPAField.helpers';
import type { PresetOption, PPAFieldProps } from './PPAField.helpers';
import { useAttributeNames } from './PPAField.hooks';
import PPAFieldDisabledTooltip from './PPAFieldDisabledTooltip';

export { getFieldKey, CONTROL_WIDTH, MAX_TAG_CHARACTER_LIMIT } from './PPAField.helpers';
export type { PpaProjectFilterInclusion, PPAFieldProps } from './PPAField.helpers';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PPAField: FC<PPAFieldProps> = ({
    fieldKey,
    inputConfig,
    value,
    disabled = false,
    onChange,
    priorProjectFilterInclusions = [],
    validationMessage = '',
    disabledMessage = '',
    controlTestId,
}) => {
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const dateFormatAlt = commonConfig?.dateFormatAlt ?? 'DD-MM-YYYY';

    const { periodTimestamps, isPeriodTimestampsLoading } = usePriceSimulatorV3Context();

    const {
        dynamicOptions,
        dynamicOptionsLoading,
        projectFilterAsyncSelectLoading,
        isProjectFilterAsyncSelect,
        asyncProjectFilterSelectValue,
        handleAttributeNamesPopupScroll,
        listHeight,
    } = useAttributeNames(inputConfig, disabled, value, priorProjectFilterInclusions);

    // Resolve the final options list: static > drop_down (normalised) > dynamic API result.
    const resolvedOptions: PPASelectOption[] =
        (inputConfig.options ??
            inputConfig.drop_down?.map((item) => ({
                label: item.label,
                value: item.id,
                start_timestamp: item.start_timestamp,
                end_timestamp: item.end_timestamp,
                dateArray: item.dateArray,
            })) ??
            dynamicOptions) ??
        [];

    // Hydrate preset period options with timestamps from get_period_start_end_timestamps.
    const resolvedOptionsWithTimestamps = useMemo<PPASelectOption[]>(() => {
        if (inputConfig.input_type !== 'date_range') return resolvedOptions;
        const tsMap = new Map(periodTimestamps.map((pt) => [pt.period_id, pt]));
        return resolvedOptions.map((opt) => {
            const ts = tsMap.get(String(opt.value));
            if (!ts) return opt;
            return { ...opt, start_timestamp: ts.start_timestamp, end_timestamp: ts.end_timestamp };
        });
    }, [inputConfig.input_type, resolvedOptions, periodTimestamps]);

    /** When timestamps load after a preset was chosen, backfill range + selection for submit validation. */
    useEffect(() => {
        if (inputConfig.input_type !== 'date_range') return;
        if (!value?.period || value.period === 'custom_date') return;
        if (value.selection) return;

        const selectedOption = resolvedOptionsWithTimestamps.find(
            (opt) => String(opt.value) === String(value.period),
        );
        if (selectedOption?.start_timestamp == null || selectedOption?.end_timestamp == null) return;

        const presetOption = selectedOption as PresetOption;
        const presetRange = getPresetDisplayRange(presetOption);
        const selectionJson = getPresetSimulationSelectionJson(presetOption);
        if (!selectionJson) return;

        onChange(fieldKey, {
            ...value,
            range: presetRange ?? [null, null],
            selection: selectionJson,
        });
    }, [
        inputConfig.input_type,
        value?.period,
        value?.selection,
        resolvedOptionsWithTimestamps,
        fieldKey,
        onChange,
        value,
    ]);

    // Auto-default Data Source when there is exactly one option (create flow starts empty).
    const isDataSourceField =
        inputConfig.input_id === 'data_source' && inputConfig.input_type === 'singleselect';

    const loneOptionValue = resolvedOptions[0]?.value;
    const singleDataSourceDefault =
        isDataSourceField &&
        resolvedOptions.length === 1 &&
        loneOptionValue !== undefined &&
        loneOptionValue !== null &&
        loneOptionValue !== ''
            ? String(loneOptionValue)
            : undefined;

    useLayoutEffect(() => {
        if (!isDataSourceField || disabled || dynamicOptionsLoading) return;
        if (singleDataSourceDefault === undefined) return;
        const isEmpty = value === undefined || value === null || value === '';
        if (isEmpty) onChange(fieldKey, singleDataSourceDefault);
    }, [isDataSourceField, disabled, dynamicOptionsLoading, singleDataSourceDefault, value, fieldKey, onChange]);

    // Helpers for the date_range control.
    const updateDateRange = (range: [any, any]) => onChange(fieldKey, { ...value, range });
    const updateTimePeriodSelection = (selection: string) => onChange(fieldKey, { ...value, selection });

    const inlineError =
        validationMessage &&
        (inputConfig.input_type === 'string' || inputConfig.input_type === 'date_range') ? (
            <p className="m-0 pl-[200px] text-[13px] leading-[20px] text-redAlt">{validationMessage}</p>
        ) : null;

    const disabledTooltipTitle = disabled && disabledMessage ? disabledMessage : '';

    const fieldLabel = (
        <p className="text mr-5 font-xs w-[180px] text-right text-blackAlt">
            <span className="text-redAlt">* </span>
            {inputConfig.input_label}
            {inputConfig.info ? (
                <Tooltip color="black" title={inputConfig.info} placement="top" trigger="hover">
                    <InfoCircleOutlined
                        style={{ cursor: 'pointer', position: 'relative', marginLeft: 4, paddingRight: 4 }}
                    />
                </Tooltip>
            ) : null}
            :
        </p>
    );
    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center">
                    {fieldLabel}

                    {/* Plain text input */}
                    {inputConfig.input_type === 'string' && (
                        <Input
                            disabled={disabled}
                            value={value}
                            placeholder={inputConfig.placeholder}
                            status={validationMessage ? 'error' : undefined}
                            style={{ fontSize: 13, width: CONTROL_WIDTH, borderRadius: 6 }}
                            data-testid={controlTestId}
                            onChange={(e) => onChange(fieldKey, e.target.value)}
                        />
                    )}

                    {/* Date-range: a period dropdown + optional custom date picker */}
                    {inputConfig.input_type === 'date_range' && (
                        <div className="flex gap-3 flex-grow items-center">
                            <CustomSelect
                                className="w-40"
                                disabled={disabled}
                                loading={dynamicOptionsLoading || isPeriodTimestampsLoading}
                                value={value?.period}
                                placeholder={inputConfig.placeholder}
                                options={resolvedOptionsWithTimestamps}
                                onChange={(period, optionFromSelect: any) => {
                                    if (period === 'custom_date') {
                                        onChange(fieldKey, {
                                            ...value,
                                            period,
                                            range: [null, null],
                                            selection: undefined,
                                        });
                                        return;
                                    }
                                    const selectedOption =
                                        resolvedOptionsWithTimestamps.find(
                                            (opt) => String(opt.value) === String(period),
                                        ) ?? optionFromSelect;

                                    const presetRange = getPresetDisplayRange(selectedOption);
                                    const selectionJson = getPresetSimulationSelectionJson(selectedOption);
                                    onChange(fieldKey, {
                                        ...value,
                                        period,
                                        range: presetRange ?? [null, null],
                                        selection: selectionJson,
                                    });
                                }}
                            />

                            {PRESET_PERIOD_IDS.includes(value?.period) &&
                                value?.range?.[0] != null &&
                                value?.range?.[1] != null && (
                                    <p className="font-xs">
                                        {dayjs(value.range[0]).format(dateFormatAlt)}{' '}
                                        -{' '}
                                        {dayjs(value.range[1]).format(dateFormatAlt)}
                                    </p>
                                )}

                            {value?.period === 'custom_date' && (
                                <DateRangePicker
                                    isEffectiveDateBetweenSimulationDate={() => true}
                                    simulationDateRange={value?.range ?? [null, null]}
                                    setSimulationDateRange={updateDateRange}
                                    setSimulationTimePeriodSelection={updateTimePeriodSelection}
                                    simulationTimePeriodSelection={value?.selection}
                                    maxRangeWeeks={PPA_MAX_SIMULATION_WEEKS}
                                />
                            )}
                        </div>
                    )}

                    {/* Single-select dropdown */}
                    {inputConfig.input_type === 'singleselect' && (
                        <PPAFieldDisabledTooltip title={disabledTooltipTitle}>
                            <CustomSelect
                                disabled={disabled}
                                loading={projectFilterAsyncSelectLoading}
                                value={asyncProjectFilterSelectValue}
                                placeholder={inputConfig.placeholder}
                                options={resolvedOptions}
                                style={{ width: CONTROL_WIDTH }}
                                listHeight={listHeight}
                                showArrow
                                showSearch={false}
                                onPopupScroll={
                                    isProjectFilterAsyncSelect ? handleAttributeNamesPopupScroll : undefined
                                }
                                onChange={(selected) => {
                                    if (!isProjectFilterAsyncSelect) {
                                        onChange(fieldKey, selected);
                                        return;
                                    }
                                    onChange(
                                        fieldKey,
                                        selected != null && selected !== ''
                                            ? String(selected as string | number)
                                            : selected,
                                    );
                                }}
                            />
                        </PPAFieldDisabledTooltip>
                    )}

                    {/* Multi-select dropdown – shows up to 2 tags then "+N more" */}
                    {inputConfig.input_type === 'multiselect' && (
                        <PPAFieldDisabledTooltip title={disabledTooltipTitle}>
                            <CustomSelect
                                disabled={disabled}
                                loading={projectFilterAsyncSelectLoading}
                                mode="multiple"
                                value={asyncProjectFilterSelectValue}
                                placeholder={inputConfig.placeholder}
                                options={resolvedOptions}
                                style={{ width: CONTROL_WIDTH }}
                                listHeight={listHeight}
                                maxTagCount={2}
                                maxTagPlaceholder={(remaining) => (
                                    <MaxTagComponent remainingCount={remaining.length || 0} />
                                )}
                                showArrow
                                showSearch={false}
                                maxTagTextLength={MAX_TAG_CHARACTER_LIMIT}
                                onPopupScroll={
                                    isProjectFilterAsyncSelect ? handleAttributeNamesPopupScroll : undefined
                                }
                                onChange={(selected) => {
                                    if (!isProjectFilterAsyncSelect) {
                                        onChange(fieldKey, selected);
                                        return;
                                    }
                                    const arr = Array.isArray(selected) ? selected : [];
                                    onChange(fieldKey, dedupeSelectValuesByKey(arr).map((v) => String(v)));
                                }}
                            />
                        </PPAFieldDisabledTooltip>
                    )}

                    {/* Boolean toggle: Public ↔ Private */}
                    {inputConfig.input_type === 'boolean' && (
                        <div className="flex items-center justify-between">
                            <span className="mr-2 font-xs text-blackAlt">Public</span>
                            <Switch
                                disabled={disabled}
                                checked={Boolean(value)}
                                onChange={(checked) => onChange(fieldKey, checked)}
                            />
                            {' '}
                            <div>{getAccessIcon(true, { top: 0 })}</div>
                            <span className="ml-2 font-xs text-blackAlt">Private</span>
                        </div>
                    )}
            </div>
            {inlineError}
        </div>
    );
};

export default PPAField;
